import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ChevronUp, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { TestResults } from '../../types';

interface ResultsSummaryProps {
  results: TestResults;
}

const ResultsSummary: React.FC<ResultsSummaryProps> = ({ results }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  const getStatusColor = (status: 'Ready' | 'Almost Ready' | 'Not Ready') => {
    switch(status) {
      case 'Ready': return 'bg-success-500 text-white border-success-200';
      case 'Almost Ready': return 'bg-warning-500 text-white border-warning-200';
      case 'Not Ready': return 'bg-error-500 text-white border-error-200';
      default: return 'bg-gray-500 text-white border-gray-200';
    }
  };

  const getStatusIcon = (status: 'Ready' | 'Almost Ready' | 'Not Ready') => {
    switch(status) {
      case 'Ready': return <TrendingUp className="w-5 h-5" />;
      case 'Almost Ready': return <Minus className="w-5 h-5" />;
      case 'Not Ready': return <TrendingDown className="w-5 h-5" />;
      default: return <Minus className="w-5 h-5" />;
    }
  };

  const getFeedbackContent = (status: 'Ready' | 'Almost Ready' | 'Not Ready') => {
    switch(status) {
      case 'Ready':
        return {
          title: 'Excellent Export Readiness!',
          summary: 'Your business demonstrates strong export readiness across most categories.',
          details: [
            'You appear well-prepared to pursue international markets with confidence',
            'Your strong foundation in key areas positions you for export success',
            'Consider reviewing any specific categories that scored lower to ensure comprehensive readiness',
            'You may want to start with pilot markets to test your export strategy'
          ]
        };
      case 'Almost Ready':
        return {
          title: 'Good Progress Towards Export Readiness',
          summary: 'Your business shows promising export potential but has some areas that need improvement.',
          details: [
            'You have a solid foundation but need to strengthen specific areas before fully engaging in international markets',
            'Focus on the categories with lower scores to improve your overall readiness',
            'Consider seeking targeted assistance for your weaker areas',
            'With focused improvements, you could be export-ready within 6-12 months'
          ]
        };
      case 'Not Ready':
        return {
          title: 'Significant Preparation Needed',
          summary: 'Your business requires substantial development before pursuing international markets.',
          details: [
            'Several critical export readiness areas need significant improvement',
            'Use the category breakdowns to create a targeted improvement plan',
            'Consider seeking comprehensive export development assistance',
            'Focus on building foundational capabilities before pursuing export opportunities',
            'This is normal for businesses new to exporting - with proper preparation, you can succeed'
          ]
        };
    }
  };

  const feedback = getFeedbackContent(results.overallStatus);

  return (
    <motion.div
      className="bg-white rounded-xl shadow-card overflow-hidden mb-8"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.2 }}
    >
      <div className="p-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Score Display */}
          <motion.div
            className="flex flex-col items-center justify-center bg-gradient-to-br from-primary-50 to-primary-100 p-8 rounded-xl"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.4, duration: 0.5 }}
          >
            <h3 className="text-lg font-medium text-gray-700 mb-4">Overall Export Readiness Score</h3>
            
            <div className="relative">
              <motion.div 
                className="text-6xl font-bold text-primary-600 mb-4"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.6, type: "spring", stiffness: 200 }}
              >
                {Math.round(results.overallPercentage)}%
              </motion.div>
              
              <motion.div
                className="absolute -inset-4 border-4 border-primary-200 rounded-full"
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ delay: 0.8, duration: 0.8 }}
                style={{
                  background: `conic-gradient(from 0deg, #1658ff ${results.overallPercentage * 3.6}deg, #e5e7eb ${results.overallPercentage * 3.6}deg)`
                }}
              />
            </div>
            
            <motion.div 
              className={`px-6 py-3 rounded-full flex items-center gap-2 ${getStatusColor(results.overallStatus)}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1 }}
            >
              {getStatusIcon(results.overallStatus)}
              <span className="font-bold">{results.overallStatus}</span>
            </motion.div>
          </motion.div>
          
          {/* Feedback Section */}
          <div className="lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.5 }}
            >
              <h3 className="text-xl font-bold text-gray-800 mb-4">{feedback.title}</h3>
              
              <p className="text-gray-700 mb-6 leading-relaxed">
                {feedback.summary}
              </p>
              
              <motion.button
                className="flex items-center gap-2 text-primary-600 hover:text-primary-700 font-medium transition-colors"
                onClick={() => setIsExpanded(!isExpanded)}
                whileHover={{ x: 4 }}
                whileTap={{ scale: 0.98 }}
              >
                <span>{isExpanded ? 'Show Less' : 'Show Detailed Analysis'}</span>
                {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </motion.button>
              
              <AnimatePresence>
                {isExpanded && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                    className="mt-6 bg-gray-50 rounded-lg p-6"
                  >
                    <h4 className="font-semibold text-gray-800 mb-4">Detailed Analysis:</h4>
                    <ul className="space-y-3">
                      {feedback.details.map((detail, index) => (
                        <motion.li
                          key={index}
                          className="flex items-start gap-3 text-gray-700"
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.1 }}
                        >
                          <div className="w-2 h-2 bg-primary-500 rounded-full mt-2 flex-shrink-0" />
                          <span>{detail}</span>
                        </motion.li>
                      ))}
                    </ul>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default ResultsSummary;